import { assert } from '../lib/assert';
import { Logger } from '../lib/logger';
import { LoggerNames } from '../lib/constants';
import { SimpleStorage } from '../storage-manager/storage.types';
import { SSO_CACHE_KEYS } from '../storage-manager/storage.const';
import { AuthSdkError } from '../lib/auth-sdk-error';
import { OpenIdConnectDiscovery } from '../types';
import { RequestUtil } from '../request-util/request-util';

const OUR_RESPONSE_TYPES = ['code'] as const;
export type SupportedResponseType = (typeof OUR_RESPONSE_TYPES)[number];
const OUR_RESPONSE_MODES = ['post_message', 'query'] as const;
export type SupportedResponseMode = (typeof OUR_RESPONSE_MODES)[number];
const OUR_CODE_CHALLENGE_METHODS = ['S256'] as const;
export type SupportedCodeChallengeMethod = (typeof OUR_CODE_CHALLENGE_METHODS)[number];
const OUR_GRANT_TYPES = ['authorization_code', 'refresh_token'] as const;
export type SupportedGrantType = (typeof OUR_GRANT_TYPES)[number];

export type ValidatedOidcConfig = {
  authorization_endpoint: string;
  code_challenge_methods_supported: Array<SupportedCodeChallengeMethod>;
  end_session_endpoint: string;
  grant_types_supported: Array<SupportedGrantType>;
  issuer: string;
  jwks_uri: string;
  response_modes_supported: Array<SupportedResponseMode>;
  response_types_supported: Array<SupportedResponseType>;
  token_endpoint: string;
  userinfo_endpoint: string;
};

export type OidcConfigCache = {
  expiresAt: number;
  config: ValidatedOidcConfig;
};

/**
 * A class for managing the OIDC configuration for a given environment.
 * Will introspect the .well-known/openid-configuration endpoint and validate
 * the response against our supported response types.
 *
 * Provides methods for getting the endpoints for the various parts of the OIDC flow
 */
export class OidcConfigManager {
  private readonly _issuer: string;
  private _openIdConfig: ValidatedOidcConfig | null = null;
  private readonly logger = Logger.createLogger(LoggerNames.OIDC_CONFIG_MANAGER);
  private readonly cacheStorage: SimpleStorage;

  constructor({ issuer, cacheStorage }: { issuer: string; cacheStorage: SimpleStorage }) {
    this._issuer = issuer;
    this.cacheStorage = cacheStorage;
    void this.fetchOpenIdConfig();
  }

  /**
   * Fetches the OIDC configuration from the .well-known/openid-configuration endpoint
   * Caches the response for subsequent calls
   * @private
   */
  private get openIdConfig(): Promise<ValidatedOidcConfig> {
    if (this._openIdConfig) return Promise.resolve(this._openIdConfig);

    this.logger.debug('OIDC config not cached');
    return this.fetchOpenIdConfig();
  }

  private fetchOpenIdConfig(): Promise<ValidatedOidcConfig> {
    this.logger.debug('Fetching OIDC config');
    const cachedConfig: OidcConfigCache = this.cacheStorage.getItem(SSO_CACHE_KEYS.OIDC_CONFIGURATION);
    try {
      if (cachedConfig && cachedConfig.expiresAt > Date.now() && cachedConfig.config.issuer === this._issuer) {
        this.logger.debug('Restoring OIDC configuration from cache');
        this._openIdConfig = this.validate(cachedConfig.config);
        return Promise.resolve(this._openIdConfig);
      }
    } catch {
      this.logger.info('Cached OIDC configuration is missing, invalid, or expired. Refreshing');
    }

    return RequestUtil.request(`${this._issuer}/.well-known/openid-configuration`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    }).then((config) => {
      this._openIdConfig = this.validate(config);
      const cache: OidcConfigCache = {
        // expires in an hour
        expiresAt: Date.now() + 60 * 60 * 1000,
        config,
      };
      this.cacheStorage.setItem(SSO_CACHE_KEYS.OIDC_CONFIGURATION, cache);
      return this._openIdConfig;
    });
  }

  /**
   * Validate the OIDC configuration against our supported response types
   * If none of our supported response types are supported by the server, throw an error
   *
   * @param config
   * @private
   */
  private validate(config: OpenIdConnectDiscovery): ValidatedOidcConfig {
    if (typeof config !== 'object' || config === null) {
      throw new AuthSdkError('Invalid OIDC configuration');
    }

    this.logger.debug('Validating OIDC config');
    const response_types_supported = OUR_RESPONSE_TYPES.filter((type) =>
      config.response_types_supported?.includes(type),
    );
    assert(response_types_supported.length > 0, 'No supported response types');
    const response_modes_supported = OUR_RESPONSE_MODES.filter((mode) =>
      config.response_modes_supported?.includes(mode),
    );
    assert(response_modes_supported.length > 0, 'No supported response modes');
    const code_challenge_methods_supported = OUR_CODE_CHALLENGE_METHODS.filter((method) =>
      config.code_challenge_methods_supported?.includes(method),
    );
    assert(code_challenge_methods_supported.length > 0, 'No supported code challenge methods');
    const grant_types_supported = OUR_GRANT_TYPES.filter((type) => config.grant_types_supported?.includes(type));
    assert(grant_types_supported.length > 0, 'No supported grant types');

    this.logger.debug('OIDC config validated successfully');
    return {
      ...config,
      response_types_supported,
      response_modes_supported,
      code_challenge_methods_supported,
      grant_types_supported,
    };
  }

  /**
   * Uses the OIDC configuration to get the issuer url
   *
   */
  public async issuer(): Promise<string> {
    const config = await this.openIdConfig;
    return config.issuer;
  }

  /**
   * Uses the OIDC configuration to get the authorization endpoint.
   */
  public async authorizationUrl(): Promise<string> {
    const config = await this.openIdConfig;
    return config.authorization_endpoint;
  }

  /**
   * Uses the OIDC configuration to get the end session endpoint
   */
  public async endSessionUrl(): Promise<string> {
    const config = await this.openIdConfig;
    return config.end_session_endpoint;
  }

  /**
   * Uses the OIDC configuration to get the token endpoint.
   * Uses the issuer as the base url.
   */
  public async tokenUrl(): Promise<string> {
    const config = await this.openIdConfig;
    return config.token_endpoint;
  }

  public async userInfoUrl(): Promise<string> {
    const config = await this.openIdConfig;
    return config.userinfo_endpoint;
  }

  private async _checkFieldSupported<
    TKey extends Extract<
      keyof ValidatedOidcConfig,
      | 'response_types_supported'
      | 'response_modes_supported'
      | 'code_challenge_methods_supported'
      | 'grant_types_supported'
    >,
    TValue extends ValidatedOidcConfig[TKey][number],
  >(key: TKey, value: TValue): Promise<TValue> {
    return this.openIdConfig.then((config) => {
      const configEntry = config[key] as Array<string>;
      assert(configEntry.includes(value), `Unsupported ${key}: '${value}'`);
      return value;
    });
  }

  /**
   * Validates a given response type against the OIDC configuration
   * @param responseType
   */
  public responseType<TType extends SupportedResponseType>(responseType: TType): Promise<TType> {
    return this._checkFieldSupported('response_types_supported', responseType);
  }

  /**
   * Validates a given response mode against the OIDC configuration
   * @param responseMode
   */
  public responseMode<TMode extends SupportedResponseMode>(responseMode: TMode): Promise<TMode> {
    return this._checkFieldSupported('response_modes_supported', responseMode);
  }

  /**
   * Validates a given code challenge method against the OIDC configuration
   * @param codeChallengeMethod
   */
  public codeChallengeMethod<TMethod extends SupportedCodeChallengeMethod>(
    codeChallengeMethod: TMethod,
  ): Promise<TMethod> {
    return this._checkFieldSupported('code_challenge_methods_supported', codeChallengeMethod);
  }

  /**
   * Validates a given grant type against the OIDC configuration
   * @param grantType
   */
  public grantType<TGrantType extends SupportedGrantType>(grantType: TGrantType): Promise<TGrantType> {
    return this._checkFieldSupported('grant_types_supported', grantType);
  }
}
